import React, { useEffect } from 'react';

import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { theme } from '@waglabs/base-theme';
import { Typography } from '@waglabs/typography';

import { Container } from '../../../../../../components/Container';
import { DirectBookWalkerChip } from '../../../../../../components/DirectBookWalkerChip';
import { LoadingBoneOverlay } from '../../../../../../components/LoadingBoneOverlay';
import {
  NAME_ENTER_ADDRESS,
  NAME_ADDRESS_ENTERED,
} from '../../../../../../components/Tracker/Segment/events';
import {
  CountryAlpha2ToAlpha3,
  Routes,
} from '../../../../../../constants';
import {
  useGetAuth,
  useGetUrlQuery,
  useSegmentTrack,
} from '../../../../../../hooks';
import { useGetWalkerFromUrlQuery } from '../../../../../../hooks/useGetWalkerFromUrlQuery';
import { usePutOwnerAddressMutation } from '../../../../../../slices/owners/v6';
import { petsSlice } from '../../../../../../slices/pets';
import {
  isInitialValid,
  tryCatch,
} from '../../../../../../utils';
import { ApiErrorV6 } from '../../../../../../utils/buildApiErrorPipeline';

import {
  AddressInfoForm,
  AddressInfoFormProps,
  FormFields,
  schema,
} from './components/AddressInfoForm';

export const AddressInfo = () => {
  const {
    push,
    location,
  } = useHistory();

  const queryParams = useGetUrlQuery();

  const walker = useGetWalkerFromUrlQuery();

  const pet = useSelector(
    petsSlice.selectors.getById(queryParams.petId as string),
  );

  const {
    ownerID,
  } = useGetAuth();

  const [
    executePutOwnerAddress,
    {
      isLoading: isPutOwnerAddressLoading,
    },
  ] = usePutOwnerAddressMutation();

  const {
    execute: segmentTrack,
  } = useSegmentTrack();

  useEffect(() => {
    segmentTrack({
      event: NAME_ENTER_ADDRESS,
    });
  }, []);

  const formikValues = useFormik({
    isInitialValid,
    initialValues: schema.cast(undefined),
    validationSchema: schema,
    onSubmit: async (values) => {
      formikValues.setStatus(undefined);

      const addressLine1 = values[FormFields.StreetAddress] || '';
      const addressLine2 = values[FormFields.SuiteOrApartment] || null;
      const administrativeAreaLevel1 = values[FormFields.State] || '';
      const city = values[FormFields.City] || '';
      const country = values[FormFields.Country] || CountryAlpha2ToAlpha3.US;
      const postalCode = values[FormFields.ZipCode] || '';

      const [, err] = await tryCatch(() => (
        executePutOwnerAddress({
          ownerIdentifier: String(ownerID),
          addressLine1,
          addressLine2,
          administrativeAreaLevel1,
          city,
          country,
          postalCode,
        }).unwrap()
      ));

      if (err) {
        formikValues.setStatus({
          error: (err as ApiErrorV6).detail,
        });
        return;
      }

      segmentTrack({
        event: NAME_ADDRESS_ENTERED,
        data: {
          street_1: addressLine1,
          street_2: addressLine2,
          city,
          state: administrativeAreaLevel1,
          country,
          postal_code: postalCode,
        },
      });

      push({
        pathname: Routes.SignupComplete,
        search: location.search,
      });
    },
  });

  const subtitle = `Where does ${pet?.name} need a walk?`;
  const isLoading = isPutOwnerAddressLoading;

  return (
    <Container>
      {walker.isValid && (
        <DirectBookWalkerChip
          value={walker}
          my="16px"
        />
      )}
      <Typography
        fontFamily="muliBold"
        fontSize="22px"
        color={theme.colors.charcoal}
      >
        How cute!
      </Typography>
      <Typography
        color={theme.colors.dimGray}
      >
        {subtitle}
      </Typography>

      {isLoading && (
        <LoadingBoneOverlay />
      )}
      <AddressInfoForm
        formik={formikValues as unknown as AddressInfoFormProps['formik']}
      />
    </Container>
  );
};
